import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { ISLES, ITEMS, KINGDOMS, OVERWORLD } from '@yuan-math/content';
import { achievement, type LearnerProfile } from '@yuan-math/learning';
import {
  bearing,
  currentKingdom,
  equippedItems,
  BUILDS,
  buildAccess,
  buildAt,
  buildDef,
  builtWorld,
  canBuild,
  gateOf,
  isleLandmarkAt,
  isleOf,
  isleStates,
  landmarkAt,
  nearestLandmark,
  positionKey,
  terrainAt,
  worldState,
  type Isle,
  type Kingdom,
  type KingdomStatus,
  type Position,
  type TileGrid,
} from '@yuan-math/world';
import { Avatar, type AvatarEquipment } from '@yuan-math/ui';
import { registry, useLearner } from '../state/LearnerContext.tsx';
import { KingdomStatusChip, sealClause } from './WorldMap.tsx';
import { KEYS, Scenery, TERRAIN_WORD, useCamera, useWalk } from './tiles.tsx';

/**
 * The overworld — the map you walk across (docs/rpg.md §22).
 *
 * §22 asks for a world to *explore*: towns, forests, mountains, bridges, a
 * coast. The kingdom list this sits above answers §33's questions — what is
 * open, what is sealed and why — and this answers none of them; it is the
 * travelling, and the list underneath is the index. Neither replaces the other,
 * which is also why nothing on this canvas is the only way to reach a kingdom.
 *
 * **Walking is not an access control.** Water and mountains refuse a step;
 * neither refuses a kingdom, and `validateOverworld` proves it on every
 * `npm run content:validate` by walking the real map from the starting tile.
 * A sealed kingdom is walked up to and entered exactly like an open one — the
 * gate panel says the seal is on the story, never on the lessons.
 *
 * **The isles are the one shut gate, and they are shut over nothing that
 * matters.** Four islands off the coast open on an achievement
 * (`packages/world/src/isles.ts`), and what they hold is a keepsake and a view
 * — an `Isle` has nowhere to store a concept and `validateIsles` fails the
 * build if anybody gives it one. That is the whole reason a padlock is drawn
 * here and never on a kingdom: this one is honest. The causeway to a shut isle
 * is walkable like any other ground, so a student can stand on the landing and
 * read the badge that opens it, in the badge's own words.
 *
 * **No game engine, no game loop, no assets.** A step is
 * `stepFrom(map, position, direction)` and a route is a breadth-first search,
 * both pure and both in `packages/world`; this file owns the pixels and the
 * keyboard. Terrain is a tinted CSS tile from the theme tokens and the scenery
 * on top of it is a handful of inline SVG paths, drawn the way `Mascot` and
 * `Avatar` are drawn — nothing is fetched and nothing is licensed. Which tuft
 * of grass a tile gets is derived from the tile's own coordinates, so the
 * meadow is varied, identical on every machine, and does not reshuffle itself
 * under the student on every render.
 *
 * **Walking onto a gate asks; it does not go.** Arriving at one raises "Enter
 * the Algebra Kingdom? Start / Cancel", because a step is a small movement and
 * leaving the map is a large one: a student crossing the continent with the
 * arrow keys, or tapping a tile the coast road happens to run a gate through,
 * did not necessarily mean to end up on a kingdom page with the map gone. The
 * prompt is the arrival's whole cost — Cancel leaves you standing on the gate
 * with the panel's own Enter button right there — and it is raised *only* by
 * arriving. The button and Enter go straight in, because pressing something
 * labelled "Enter Algebra Kingdom" has already said so once.
 *
 * **Where you are standing is not a fact about your mathematics**, so it is not
 * in the profile: the walk lives as long as the page does, and a reload puts
 * you at the gate of the kingdom you are actually working in. That is both the
 * more useful answer and one less field for `mergeProfiles` to have an opinion
 * about.
 */

/** How far the live region looks for a gate worth mentioning. */
const ANNOUNCE_RADIUS = 8;

/**
 * How much of the map is actually put in the DOM.
 *
 * The continent is 52×97 — a little over five thousand tiles — because the two
 * long roads are long on purpose. Rendering all of them was fine at 40×26 and
 * is not fine now: five thousand elements is a slow first paint on the busiest
 * page in the app, on the phones this is mostly read on.
 *
 * The world is an archipelago and four fifths of it is sea, which is the one
 * terrain that needs no element of its own: it cannot be walked on, so a tile
 * of it is not a click target, and the canvas is painted `--ow-water`
 * underneath so the gap between two drawn tiles *is* the sea rather than a hole
 * in it. Drawing the land alone is about 1,700 elements — fewer than the old
 * single-continent map cost, for a world twice the size.
 *
 * **Except for the shore, when there is something to put on it.** A student
 * holding a bridge has to be able to tap the sea — but only the water *beside*
 * something solid, because a bridge in mid-ocean joins nothing to nothing. One
 * ring is enough and it extends itself: a placed bridge is walkable ground, so
 * the ring moves out with it and a causeway grows a tile at a time. Drawing the
 * whole ocean instead would be ten thousand elements for a feature that needs
 * a few hundred.
 *
 * Deliberately not a window around the walker: that would be fewer elements
 * again, and it would quietly break tapping a distant tile to travel there,
 * which is how a student crosses an ocean.
 */
const isSea = (world: TileGrid, x: number, y: number): boolean =>
  (terrainAt(world, { x, y }) ?? 'water') === 'water';

/**
 * The sides of a land tile that face open water, as `"nesw"` letters.
 *
 * Empty for anything inland and for the sea itself, so only the coastline
 * carries the attribute and only the coastline pays for the surf.
 */
function shoreOf(world: TileGrid, x: number, y: number): string {
  if (isSea(world, x, y)) return '';
  let sides = '';
  if (isSea(world, x, y - 1)) sides += 'n';
  if (isSea(world, x + 1, y)) sides += 'e';
  if (isSea(world, x, y + 1)) sides += 's';
  if (isSea(world, x - 1, y)) sides += 'w';
  return sides;
}

/** Water with something solid beside it: the only water worth building on. */
function isShore(world: TileGrid, x: number, y: number): boolean {
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    if (!isSea(world, x + dx, y + dy)) return true;
  }
  return false;
}

const STATUS_WORD: Record<KingdomStatus, string> = { sealed: 'Sealed', open: 'Open', cleared: 'Cleared' };
const STATUS_ICON: Record<KingdomStatus, string> = { sealed: '🔒', open: '🚩', cleared: '👑' };

/**
 * What a gate's plate is captioned with.
 *
 * Six of the ten kingdoms are called "«Something» Kingdom", and on a signpost
 * the word is doing no work: everything on this map is a kingdom, and the
 * repeated half is what pushed a plate four tiles wide and got it sliced by the
 * edge of the frame. The kingdom's real name is what the panel below the map
 * says, what the list says and what the kingdom's own page is headed with —
 * this is a caption, and a caption is allowed to be shorter than a title.
 */
const mapLabel = (kingdom: Kingdom): string => kingdom.name.replace(/ Kingdom$/, '');

/**
 * Which isles have a trial on them, and which.
 *
 * Only the two at the ends of the long roads. Held here rather than on the
 * `Isle` type on purpose: an isle may not name mathematics (`validateIsles`),
 * and a `trialId` field would be the thin end of exactly that — the type would
 * then have somewhere to put a concept, and the argument for allowing a padlock
 * out here at all is that it does not.
 */
const TRIAL_OF: Record<string, string | undefined> = {
  'isle-north-reach': 'north-reach',
  'isle-south-reach': 'south-reach',
};

/* -------------------------------------------------------------------------- */
/* The map                                                                    */
/* -------------------------------------------------------------------------- */

const equipmentOf = (profile: LearnerProfile | null): AvatarEquipment => {
  if (!profile) return {};
  const worn = equippedItems(ITEMS, profile);
  return { held: worn.held?.art, cape: worn.cape?.art, headgear: worn.headgear?.art };
};

export function Overworld(): JSX.Element {
  const { profile, placeBuild: place } = useLearner();
  const authored = OVERWORLD;

  /*
   * Status per kingdom, derived on every render like mastery everywhere else —
   * readiness genuinely moves as retention decays. It decides what a gate's
   * chip says, and whether the gate opens: a sealed island is not this
   * student's yet. The *ground* is never the lock — a sealed gate is as
   * walkable as any other, and `validateOverworld` still proves every one of
   * them reachable — so a student can always walk up and read what it wants.
   */
  const states = useMemo(
    () =>
      new Map(
        worldState(
          KINGDOMS,
          registry.graph,
          profile?.concepts ?? {},
          profile?.rpg?.clearedBossIds ?? [],
          Date.now(),
        ).map((state) => [state.kingdomId, state]),
      ),
    [profile],
  );

  const world = useMemo(
    () => (profile ? builtWorld(authored, profile) : authored),
    [authored, profile],
  );

  const here = profile ? currentKingdom(KINGDOMS, registry, profile, Date.now()) : undefined;

  /*
   * Building (docs — `packages/world/src/build.ts`).
   *
   * The map a student walks is the *built* one: their bridges and islands are
   * written into a copy of the authored rows, so `stepFrom`, `pathBetween` and
   * the reachability search all see them without knowing what a bridge is.
   * Because every buildable terrain is walkable, the built map's walkable set
   * is a superset of the authored one — which is why letting a child edit the
   * map cannot strand a kingdom, and why nothing here needs re-validating.
   */
  const access = profile ? buildAccess(profile) : undefined;
  const [building, setBuilding] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  /*
   * The isles, derived the same way (`packages/world/src/isles.ts`).
   *
   * An isle is the only thing on this map that can be shut, and what it withholds
   * is a drawing and a view — never a lesson, which the `Isle` type has nowhere
   * to hold. The *ground* is never shut: every causeway is walkable and
   * `validateOverworld` proves it, so a student can always stand on a landing
   * and read what it is waiting for.
   */
  const isles = useMemo(
    () => new Map(isleStates(ISLES, (profile?.achievements ?? []).map((entry) => entry.id)).map((state) => [state.isleId, state])),
    [profile],
  );

  // Session state, deliberately (see the note at the top of this file). `walkTo`
  // is stable, so a tile's click handler survives every step the student takes.
  /*
   * A tapped route walks round the other gates on the way. The coast road runs
   * *through* several of them, so without this, travelling from the village to
   * the far side of the continent would carry a student into every kingdom it
   * passes.
   */
  const arriveAt = gateOf(world, here?.kingdom.id);
  const detour = useCallback((tile: Position) => landmarkAt(world, tile) !== undefined, [world]);
  const { position, walkTo, move } = useWalk(world, arriveAt, detour);
  const camera = useCamera(world, position);

  /*
   * The ground, built once.
   *
   * A thousand tiles that re-rendered on every step would make walking feel
   * like a page load; nothing about a tile depends on where the student is
   * standing, so nothing about a tile needs redrawing when they move. The
   * camera is one transform on the parent and the adventurer is one more, which
   * is the whole of what changes between one tile and the next.
   */
  /**
   * What a tap on a tile does.
   *
   * Walk, unless the student is holding a building — in which case place it,
   * and say why not if it cannot go there. A refusal is a sentence rather than
   * a shrug: "On water" tells a child what to do next, and nothing happening
   * tells them the game is broken.
   */
  /*
   * Read through a ref, so `onTile` is stable.
   *
   * The tile memo below is the reason: a thousand tiles that re-rendered
   * whenever the student picked a different building would make choosing one
   * feel like a page load, which is the note that memo already carries about
   * walking. What the handler needs — which building is held, and the profile —
   * changes often and is wanted only at the moment of a tap, which is exactly
   * what a ref is for.
   */
  const latest = useRef({ building, profile, world });
  latest.current = { building, profile, world };

  const onTile = useCallback(
    (tile: Position) => {
      const { building: held, profile: who, world: map } = latest.current;
      if (!held || !who) {
        walkTo(tile);
        return;
      }
      const verdict = canBuild(map, who, held, tile);
      if (!verdict.ok) {
        setRefusal(verdict.reason);
        return;
      }
      setRefusal(null);
      place(map, held, tile);
    },
    [place, walkTo],
  );

  const tiles = useMemo(
    () =>
      world.rows.map((row, y) =>
        [...row].map((_char, x) => {
          // The sea is drawn only where there is something to put on it.
          if (isSea(world, x, y) && !(building && isShore(world, x, y))) return null;
          const tile = { x, y };
          const kind = terrainAt(world, tile) ?? 'water';
          const landmark = landmarkAt(world, tile);
          const kingdom = landmark ? KINGDOMS.find((k) => k.id === landmark.kingdomId) : undefined;
          const status = kingdom ? states.get(kingdom.id)?.status ?? 'sealed' : undefined;
          const mine = profile ? buildAt(profile, tile) : undefined;
          const landing = isleLandmarkAt(authored.isles ?? [], tile);
          const isle = landing ? isleOf(ISLES, landing.isleId) : undefined;
          const isleShut = isle ? isles.get(isle.id)?.status !== 'open' : false;

          return (
            <div
              key={`${x},${y}`}
              className={`ow-tile ow-tile--${kind}`}
              /* Explicit, because the grid is no longer filled in order: the
                 open sea is skipped, and without a stated position every tile
                 after the first gap would slide up the map. */
              style={{ gridColumn: x + 1, gridRow: y + 1 }}
              /* Addressable by coordinate rather than by DOM index, for the
                 same reason. */
              data-x={x}
              data-y={y}
              /* Which sides of this tile face the sea, so the CSS can draw a
                 surf line along them. Computed here, where the neighbours are
                 already to hand, rather than by asking the DOM: an island with
                 no shore is a shape sitting on a colour, and the whole map is
                 islands now. */
              data-shore={shoreOf(world, x, y) || undefined}
              /* Decorative: the live region below says where you are, and a
                 thousand tiles in the accessibility tree would say nothing. */
              aria-hidden="true"
              onClick={() => onTile(tile)}
            >
              <Scenery terrain={kind} position={tile} />
              {kingdom && status ? (
                <span className={`ow-gate ow-gate--${status}`}>
                  <span className="ow-gate__mark">{kingdom.emoji}</span>
                  <span className="ow-gate__name">
                    {mapLabel(kingdom)}
                    <span className={`ow-gate__status ow-gate__status--${status}`}>
                      {STATUS_ICON[status]} {STATUS_WORD[status]}
                    </span>
                  </span>
                </span>
              ) : null}
              {mine ? (
                <span className="ow-gate ow-build">
                  <span className="ow-gate__mark">{buildDef(mine.kind)?.emoji ?? '🏠'}</span>
                </span>
              ) : null}
              {isle ? (
                <span className={`ow-gate ow-isle ow-isle--${isleShut ? 'shut' : 'open'}`}>
                  <span className="ow-gate__mark">{isleShut ? '🔒' : isle.emoji}</span>
                  <span className="ow-gate__name">
                    {isle.name}
                    <span className={`ow-gate__status ow-isle__status--${isleShut ? 'shut' : 'open'}`}>
                      {isleShut ? 'Shut' : 'Open'}
                    </span>
                  </span>
                </span>
              ) : null}
            </div>
          );
        }),
      ),
    [authored.isles, building, isles, onTile, profile, states, world],
  );

  const gate = landmarkAt(world, position);
  const gateKingdom: Kingdom | undefined = gate ? KINGDOMS.find((k) => k.id === gate.kingdomId) : undefined;
  /*
   * A sealed island does not open, and that is the one refusal on this map.
   * `openDownwards` leaves `sealed` upward closed, so refusing this gate is
   * also the "and nothing beyond it" half of the rule — there is no open
   * kingdom further along the road to have let through by accident.
   *
   * The link still goes to the kingdom, because a sealed kingdom's own page is
   * the gate rather than the island: it states the seal in full and hands over
   * the concepts that break it. What the seal refuses is what is behind it —
   * the village, the regions, the quest line, the boss. So the button changes
   * its words rather than disappearing, and a student walking with the arrow
   * keys still has somewhere for `Enter` to take them.
   */
  const gateSealed = gateKingdom !== undefined && states.get(gateKingdom.id)?.status === 'sealed';
  const enterHref = gateKingdom ? `/adventure/kingdoms/${gateKingdom.id}` : undefined;
  const enterRef = useRef<HTMLAnchorElement | null>(null);

  /*
   * The gate an arrival has raised a question about (docs/rpg.md §22).
   *
   * Arriving somewhere still *does* something — it is not a tile you have to
   * find a button on — but what it does is ask, because walking is cheap and
   * leaving the map is not. Held as the kingdom's id and matched against the
   * gate actually underfoot, so walking on with the question still up simply
   * puts it away: there is no second thing to keep in step with where the
   * student is standing.
   *
   * Session state, like the position itself: whether somebody paused at a gate
   * is not a fact about their mathematics.
   */
  const [asking, setAsking] = useState<string | undefined>(undefined);
  const askedKingdom = gateKingdom && asking === gateKingdom.id ? gateKingdom : undefined;
  /*
   * The clauses a sealed kingdom is still waiting on, in the same words the
   * kingdom page and the world list use — one sentence about a seal, said the
   * same way everywhere, so a student is never told two different things about
   * what they have left to do.
   */
  const askedSealed = askedKingdom !== undefined && states.get(askedKingdom.id)?.status === 'sealed';
  const askedSeal = askedSealed ? (states.get(askedKingdom.id)?.missing ?? []).map(sealClause) : [];

  /*
   * Guarded by the tile the student was put down on, which here is the gate of
   * the kingdom they are working in: a "first render" flag would greet that
   * kingdom's gate with a prompt the moment the page loaded.
   */
  const handled = useRef(positionKey(arriveAt));
  useEffect(() => {
    const key = positionKey(position);
    if (handled.current === key) return;
    handled.current = key;
    setAsking(landmarkAt(world, position)?.kingdomId);
  }, [position, world]);

  /*
   * The question takes the focus, and Cancel hands it back to the map — a
   * student walking with the arrow keys must not be left pressing them at a
   * dialog, nor stranded off the map once they have dismissed one.
   */
  const startRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (askedKingdom) startRef.current?.focus();
  }, [askedKingdom]);

  const cancel = useCallback(() => {
    setAsking(undefined);
    camera.frameRef.current?.focus();
  }, [camera.frameRef]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const direction = KEYS[event.key];
    if (direction) {
      event.preventDefault();
      move(direction);
      return;
    }
    if ((event.key === 'Enter' || event.key === ' ') && enterRef.current) {
      event.preventDefault();
      enterRef.current.click();
    }
  };

  /* ------------------------------------------------------------ the words --- */

  /*
   * The first clause of a sealed gate's requirement, for the live region. One
   * clause rather than all of them: this sentence is read aloud on every step
   * a student takes near the gate, and the full statement belongs on the card.
   */
  const gateSeal = (() => {
    if (!gateKingdom) return '';
    const state = states.get(gateKingdom.id);
    if (state?.status !== 'sealed') return '';
    // No invented fallback: a sealed kingdom always has a clause, and if one
    // ever did not, saying nothing beats saying "needs an earlier boss".
    const first = state.missing[0];
    return first ? sealClause(first).headline : '';
  })();

  const terrain = terrainAt(world, position) ?? 'grass';
  const near = nearestLandmark(world, position, ANNOUNCE_RADIUS);
  const nearKingdom = near ? KINGDOMS.find((k) => k.id === near.landmark.kingdomId) : undefined;

  /*
   * The isle underfoot, and what it is waiting for.
   *
   * A shut isle names its achievement in words — the badge's own title, so a
   * student reading "Needs: Mastered It" on the sand and looking at
   * `/achievements` is looking for one thing rather than two. A padlock that
   * declines to say why is the version of this that tells a child they are not
   * good enough and leaves them to guess; the same reason a kingdom's seal is
   * stated as "82% of Starter Village skills" rather than drawn as a chain.
   */
  const landingHere = isleLandmarkAt(world.isles ?? [], position);
  const isleHere: Isle | undefined = landingHere ? isleOf(ISLES, landingHere.isleId) : undefined;
  const isleShutHere = isleHere ? isles.get(isleHere.id)?.status !== 'open' : false;
  const isleKey = isleHere ? achievement(isleHere.requiresAchievementId) : undefined;

  const sentence = isleHere
    ? isleShutHere
      ? `${isleHere.name}, shut. Needs: ${isleKey?.title ?? isleHere.requiresAchievementId}.`
      : `${isleHere.name}, open.`
    : gateKingdom
      ? `${gateKingdom.name}, ${STATUS_WORD[states.get(gateKingdom.id)?.status ?? 'sealed'].toLowerCase()}${
          gateSeal ? `. ${gateSeal}` : ''
        }. Press Enter to go in.`
      : nearKingdom && near
        ? `${TERRAIN_WORD[terrain]}. ${nearKingdom.name} is ${near.steps} ${near.steps === 1 ? 'step' : 'steps'} ${bearing(position, near.landmark)}.`
        : `${TERRAIN_WORD[terrain]}. No kingdom within ${ANNOUNCE_RADIUS} steps.`;

  return (
    <div className="overworld">
      {/*
        `role="application"` because the arrow keys are the content: in browse
        mode a screen reader would swallow every one of them and the map would
        be a picture of a world nobody could walk. It is a defensible use of a
        heavy role precisely because it is not the only route — the kingdom
        list below this map reaches all ten kingdoms as ordinary links, carries
        the statuses, and states every seal in mathematics.
      */}
      <div
        className="overworld__viewport"
        role="application"
        aria-roledescription="Walkable map"
        aria-label="The overworld. Arrow keys or W A S D to walk, Enter to go into a kingdom you are standing on."
        aria-describedby="overworld-here"
        tabIndex={0}
        onKeyDown={onKeyDown}
        style={{ ['--ow-width' as string]: world.width, ['--ow-height' as string]: world.height }}
        ref={camera.frameRef}
      >
        <div className="overworld__canvas" style={{ transform: camera.transform }} ref={camera.canvasRef}>
          {tiles}

          <div
            className="overworld__walker"
            style={{
              transform: `translate3d(calc(${position.x} * var(--ow-tile)), calc(${position.y} * var(--ow-tile)), 0)`,
            }}
          >
            <Avatar
              config={profile?.rpg?.character?.avatar}
              classId={profile?.rpg?.character?.classId}
              equipment={equipmentOf(profile)}
              size={34}
            />
          </div>
        </div>
      </div>

      {/*
        The gate's question. Answered with the panel's own link rather than a
        second route of its own, so "what a gate means" is written down once:
        Start clicks the same anchor the button and Enter click.

        A seal withholds the island: an open gate offers Start, and a sealed one
        offers what opens it instead. Neither is a dead end — the sealed card
        states the requirement as mathematics and its button goes to the page
        that hands over the concepts, none of which is sealed.
      */}
      {askedKingdom ? (
        <div
          className="ow-ask"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ow-ask-title"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              cancel();
            }
          }}
        >
          <div className="ow-ask__card">
            <KingdomStatusChip status={states.get(askedKingdom.id)?.status ?? 'sealed'} />
            <h2 className="ow-ask__title" id="ow-ask-title">
              <span aria-hidden="true">{askedKingdom.emoji}</span>{' '}
              {askedSealed ? `The ${askedKingdom.name} is sealed` : `Enter the ${askedKingdom.name}?`}
            </h2>
            <p className="ow-ask__body">{askedKingdom.tagline}</p>
            {/*
              What the seal is waiting for, said as mathematics, at the moment
              a student is deciding to go in.

              This is "finish an island to unlock the next", said honestly: the
              requirement is named in skills rather than in XP, gold or a level,
              and the line it does not cross is the mathematics. The island is
              withheld; the concepts underneath it are not, and the button below
              goes to the page that lists them.
            */}
            {askedSeal.length > 0 ? (
              <ul className="ow-ask__seal">
                {askedSeal.map((clause, index) => (
                  <li key={index}>
                    {clause.headline}
                    {clause.detail ? <span className="ow-ask__seal-detail"> {clause.detail}</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="ow-ask__actions">
              <button ref={startRef} type="button" className="btn btn--primary" onClick={() => enterRef.current?.click()}>
                {askedSealed ? 'See what opens it' : 'Start'}
              </button>
              <button type="button" className="btn btn--ghost" onClick={cancel}>
                Cancel
              </button>
            </div>
            <p className="ow-ask__note">
              {askedSealed
                ? 'The island is sealed; the skills it is waiting for are not — every one of them is open to you now.'
                : 'Cancel leaves you standing at the gate — nothing here is a lock.'}
            </p>
          </div>
        </div>
      ) : null}

      {/* ------------------------------------------------------- the panel --- */}

      <div className="overworld__bar">
        <p className="overworld__here" id="overworld-here" role="status">
          {sentence}
        </p>

        {gateKingdom && enterHref ? (
          <Link
            ref={enterRef}
            to={enterHref}
            className={`btn ${gateSealed ? 'btn--secondary' : 'btn--primary'} btn--sm overworld__enter`}
          >
            <span aria-hidden="true">{gateSealed ? '🔒' : gateKingdom.emoji}</span>{' '}
            {gateSealed ? `See what opens ${gateKingdom.name}` : `Enter ${gateKingdom.name}`}
          </Link>
        ) : null}
      </div>

      {/*
        Standing on an isle. Two things are said here and a third is not.

        Said: what the place is, and — if it is shut — the achievement that
        opens it, by the badge's own title, with a link to the page that lists
        it. Not said: any suggestion that the mathematics is out here. It never
        is; the link goes to the achievements page, which is a record of work
        already done, and every lesson on this platform is on the mainland
        behind no lock at all.
      */}
      {isleHere ? (
        <div className={`ow-isle-card ow-isle-card--${isleShutHere ? 'shut' : 'open'}`}>
          <h3 className="ow-isle-card__title">
            <span aria-hidden="true">{isleShutHere ? '🔒' : isleHere.emoji}</span> {isleHere.name}
          </h3>
          <p className="ow-isle-card__flavor">{isleHere.flavor}</p>
          {isleShutHere ? (
            <p className="ow-isle-card__needs">
              <strong>Needs:</strong> {isleKey?.title ?? isleHere.requiresAchievementId}
              {isleKey ? ` — ${isleKey.description}` : null}{' '}
              <Link to="/achievements">See your achievements</Link>
            </p>
          ) : (
            <p className="ow-isle-card__open">
              Open — you earned <strong>{isleKey?.title ?? isleHere.requiresAchievementId}</strong>. Anything it holds
              is in your <Link to="/adventure/character">wardrobe</Link>.
            </p>
          )}
          {/*
            The trial, on the one isle that has one — and offered whether or not
            the isle has opened. The keepsake is behind the achievement; the
            mathematics never is, which is the whole reason a padlock is
            allowable out here at all.
          */}
          {TRIAL_OF[isleHere.id] ? (
            <p className="ow-isle-card__trial">
              <Link to={`/adventure/trial/${TRIAL_OF[isleHere.id]}`} className="btn btn--primary btn--sm">
                <span aria-hidden="true">{isleHere.emoji}</span> Take the trial
              </Link>
              <span className="ow-isle-card__trial-note">
                Pitched at the mathematics you have been working on. Open to anyone who walked the road, shut isle or
                not.
              </span>
            </p>
          ) : null}
          <p className="ow-isle-card__note">
            Isles hold keepsakes and a view. Every lesson and every practice page is back on the mainland, and none of
            them is ever locked.
          </p>
        </div>
      ) : null}

      {/*
        The workshop.
        
        Shown to everybody, locked or not, and this is the point of it: a tool
        a student cannot see is a tool they cannot work towards. Locked, it
        states the requirement in words and draws how far along they are — "6
        of 10 questions answered" is a plan, and a padlock with no sentence is
        a wall. Unlocked, it is three buttons.
      */}
      {access ? (
        <div className={`ow-build-bar ${access.unlocked ? '' : 'ow-build-bar--locked'}`}>
          {/* h2, not h3: the workshop is a section of the map page, whose own
              title is the h1. A skipped level leaves a screen reader with a
              section it cannot navigate to. */}
          <h2 className="ow-build-bar__title">
            <span aria-hidden="true">🧱</span> Build
          </h2>

          {access.unlocked ? (
            <>
              <div className="ow-build-bar__kinds" role="group" aria-label="What to build">
                {BUILDS.map((def) => (
                  <button
                    key={def.kind}
                    type="button"
                    className={`btn btn--sm ${building === def.kind ? 'btn--primary' : 'btn--ghost'}`}
                    aria-pressed={building === def.kind}
                    onClick={() => {
                      setRefusal(null);
                      setBuilding(building === def.kind ? null : def.kind);
                    }}
                  >
                    <span aria-hidden="true">{def.emoji}</span> {def.name}
                  </button>
                ))}
              </div>
              <p className="ow-build-bar__hint" role="status">
                {refusal
                  ? refusal
                  : building
                    ? `${buildDef(building)?.hint} Tap a tile to place it.`
                    : 'Pick something, then tap a tile. Tapping a tile without picking walks you there.'}
              </p>
            </>
          ) : (
            <>
              <p className="ow-build-bar__needs">
                <strong>{access.requirement}</strong> Bridges, islands and houses — you can put them anywhere on the
                map, and nothing you build ever gets in your way.
              </p>
              <p className="ow-build-bar__progress">
                <span
                  className="ow-build-bar__meter"
                  role="progressbar"
                  aria-valuenow={access.done}
                  aria-valuemin={0}
                  aria-valuemax={access.total}
                  aria-label="Questions answered towards building"
                >
                  <span
                    className="ow-build-bar__fill"
                    style={{ width: `${(access.done / access.total) * 100}%` }}
                  />
                </span>
                {access.done} of {access.total} questions answered
              </p>
            </>
          )}
        </div>
      ) : null}

      <p className="overworld__help">
        <span aria-hidden="true">⌨️</span> Arrow keys or <kbd>W</kbd> <kbd>A</kbd> <kbd>S</kbd> <kbd>D</kbd> to walk ·
        tap a tile to walk there · walk onto a gate and it asks before it takes you in, or press <kbd>Enter</kbd> to go
        straight there while you are standing on one. The sea and the mountains turn you back; nothing else on this map
        ever will — every kingdom below is open to visit, sealed or not.
      </p>
    </div>
  );
}
